import {
  Add16,
  ChevronDown16,
  ChevronRight16,
  EarthFilled16,
  ViewOff16,
} from '@carbon/icons-react';
import _ from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { ContextType, ContextTypeReverse } from 'api/conversations';
import {
  ProjectConversation,
  ProjectGlobalConversation,
} from 'api/project-conversations';
import { Tag } from 'components/atoms/tag';
import { ConversationIcon } from 'components/organisms/projects/project-conversation/conversation-components';
import {
  AccountsSidebar,
  AddInProjectButton,
  ProjectChevronWrapper,
  ProjectEmptyState,
  ProjectName,
  ProjectNameAndChevronWrapper,
  ProjectNameWrapper,
  ProjectWrapper,
  SideBarItem,
  SideBarItemIcon,
  SideBarItemName,
  SideBarSection,
  SideBarSectionTitle,
} from '../common/side-bar';
import { CpChatActiveContext, CpChatRenderInput, CpProjectChat } from './types';

const ConversationsList = styled(AccountsSidebar)`
  gap: 0;
  width: 350px;
  border-radius: 0;
  [data-preview-mode='false'] & {
    height: 55vh;
  }
  @media (max-width: 768px) {
    border: none;
    border-right: 1px solid var(--cp-border-subtle);
  }
  @media (max-width: 480px) {
    width: 100%;
    border: none;
    border-top: 1px solid var(--cp-border-subtle);
  }
`;

export const ChatListForEachProject: React.FC<{
  chat: CpProjectChat;
  activeConversation:
    | { id: number; type: keyof typeof ContextType }
    | undefined;
  onProjectConverationClick: (pc: ProjectConversation) => void;
  onEntityConversationClick: (ec: ProjectGlobalConversation) => void;
  onAddConversationClick: VoidFunction;
  isActiveConversationAPartOfProject?: boolean;
}> = ({
  chat,
  onEntityConversationClick,
  onProjectConverationClick,
  activeConversation,
  onAddConversationClick,
  isActiveConversationAPartOfProject,
}) => {
  const conversations = useMemo(() => {
    return _.groupBy(chat.projectConversations, 'isPartOfConversation');
  }, [chat.projectConversations]);

  const [isExpanded, setIsExpanded] = useState(
    isActiveConversationAPartOfProject,
  );

  useEffect(() => {
    setIsExpanded(isActiveConversationAPartOfProject);
  }, [isActiveConversationAPartOfProject]);
  const noConversations = useMemo(
    () =>
      (conversations?.true?.length ?? 0) === 0 &&
      (conversations?.false?.length ?? 0) === 0 &&
      (chat.entityConversations?.length ?? 0) === 0,
    [
      chat.entityConversations?.length,
      conversations?.false?.length,
      conversations?.true?.length,
    ],
  );

  return (
    <ProjectWrapper isExpanded={!!isExpanded}>
      <ProjectNameWrapper>
        <ProjectNameAndChevronWrapper onClick={() => setIsExpanded((p) => !p)}>
          <ProjectChevronWrapper>
            {isExpanded ? <ChevronDown16 /> : <ChevronRight16 />}
          </ProjectChevronWrapper>
          <ProjectName>{chat.project.projectName}</ProjectName>
        </ProjectNameAndChevronWrapper>
        <AddInProjectButton
          onClick={(event) => {
            event.stopPropagation();
            onAddConversationClick();
          }}
        >
          <Add16 />
        </AddInProjectButton>
      </ProjectNameWrapper>

      {isExpanded ? (
        <>
          {noConversations ? (
            <ProjectEmptyState>
              <FormattedMessage id="projects.conversations.new.empty.title" />
            </ProjectEmptyState>
          ) : (
            <></>
          )}
          {conversations?.true?.length ? (
            <SideBarSection>
              <SideBarSectionTitle>
                <FormattedMessage id="projects.conversations.member" />
              </SideBarSectionTitle>
              {conversations?.true?.length ? (
                conversations.true?.map((e) => (
                  <SideBarItem
                    active={
                      activeConversation?.id === e.conversationId &&
                      activeConversation?.type === 'PROJECT_CONVERSATIONS'
                    }
                    onClick={() => onProjectConverationClick(e)}
                  >
                    <SideBarItemIcon>
                      {e.private ? <ViewOff16 /> : <EarthFilled16 />}
                    </SideBarItemIcon>
                    <SideBarItemName className="rl-cp-conversation-name rl-cp-project-conversation-name rl-cp-part-of-conversation">
                      {e.conversationName}
                    </SideBarItemName>
                  </SideBarItem>
                ))
              ) : (
                <ProjectEmptyState>
                  <FormattedMessage id="projects.conversations.new.empty.title" />
                </ProjectEmptyState>
              )}
            </SideBarSection>
          ) : (
            <></>
          )}

          {conversations?.false?.length ? (
            <SideBarSection>
              <SideBarSectionTitle>
                <FormattedMessage id="projects.conversations.non_member" />
              </SideBarSectionTitle>
              {conversations.false?.length ? (
                conversations.false?.map((e) => (
                  <SideBarItem
                    onClick={() => onProjectConverationClick(e)}
                    active={
                      activeConversation?.id === e.conversationId &&
                      activeConversation?.type === 'PROJECT_CONVERSATIONS'
                    }
                  >
                    <SideBarItemIcon>
                      {e.private ? <ViewOff16 /> : <EarthFilled16 />}
                    </SideBarItemIcon>
                    <SideBarItemName>{e.conversationName}</SideBarItemName>
                    {e?.unreadMessages ? (
                      <Tag color="magenta">{e?.unreadMessages}</Tag>
                    ) : (
                      <></>
                    )}
                  </SideBarItem>
                ))
              ) : (
                <ProjectEmptyState>
                  <FormattedMessage id="projects.conversations.new.empty.title" />
                </ProjectEmptyState>
              )}
            </SideBarSection>
          ) : (
            <></>
          )}

          {chat.entityConversations?.length ? (
            <SideBarSection>
              <SideBarSectionTitle>
                <FormattedMessage id="projects.conversations.other" />
              </SideBarSectionTitle>
              {chat?.entityConversations?.length ? (
                chat?.entityConversations?.map((e) => (
                  <SideBarItem
                    onClick={() => onEntityConversationClick(e)}
                    active={
                      activeConversation?.id === e.entityId &&
                      activeConversation?.type ===
                        ContextTypeReverse[e.entityType]
                    }
                  >
                    <SideBarItemIcon>
                      <ConversationIcon conversation={e} staticIcon />
                    </SideBarItemIcon>
                    <SideBarItemName>{e.entityName}</SideBarItemName>
                    {e?.unreadMessages ? (
                      <Tag color="magenta">{e?.unreadMessages}</Tag>
                    ) : (
                      <></>
                    )}
                  </SideBarItem>
                ))
              ) : (
                <ProjectEmptyState>
                  <FormattedMessage id="projects.conversations.new.empty.title" />
                </ProjectEmptyState>
              )}
            </SideBarSection>
          ) : (
            <></>
          )}
        </>
      ) : (
        <></>
      )}
    </ProjectWrapper>
  );
};

const isActiveConversationAPartOfProject = (
  activeConversation: CpChatActiveContext,
  project: CpProjectChat,
) => {
  const id = activeConversation?.id;

  if (!id) {
    return false;
  }

  const ids = project?.projectConversations
    ?.map?.((x) => x.conversationId)
    ?.concat?.(project?.entityConversations?.map?.((x) => x.entityId));

  const isPresent = ids?.includes?.(id);

  return isPresent;
};

export const CpChatSidePane: React.FC<{
  activeConversation: CpChatActiveContext;
  onProjectConverationClick: (pc: ProjectConversation) => void;
  onEntityConversationClick: (
    ec: ProjectGlobalConversation,
    pid: number,
  ) => void;
  onAddConversationClick: (p: number) => void;

  data: CpChatRenderInput;
}> = ({
  activeConversation,
  onProjectConverationClick,
  onEntityConversationClick,
  onAddConversationClick,

  data,
}) => {
  return (
    <ConversationsList>
      {_.sortBy(
        data?.projectChats ?? [],
        (p) => p?.project?.projectId ?? 0,
      )?.map?.((e) => (
        <ChatListForEachProject
          key={e?.project?.projectId}
          onAddConversationClick={() =>
            onAddConversationClick(e?.project?.projectId)
          }
          onProjectConverationClick={onProjectConverationClick}
          onEntityConversationClick={(x) =>
            onEntityConversationClick(x, e.project.projectId)
          }
          chat={e}
          activeConversation={activeConversation}
          isActiveConversationAPartOfProject={isActiveConversationAPartOfProject(
            activeConversation,
            e,
          )}
        />
      ))}
    </ConversationsList>
  );
};
